import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { ReportAccessGuard } from '../../components/ReportAccessGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { WeeklyAttendanceReportCard } from '../../components/WeeklyAttendanceReportCard';
import { REPORT_ACCESS_CODES } from '../../config/reportCapabilities';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { getWeeklyAttendanceReports } from '../../services/attendanceService';
import type { WeeklyAttendanceEmployeeReport } from '../../types/weeklyAttendanceReport';
import { attendanceErrorMessage } from '../../utils/attendanceErrors';
import {
  formatWeekRangeLabel,
  getIsoWeekBounds,
  shiftIsoWeek,
} from '../../utils/weeklyAttendanceReport';

type Props = NativeStackScreenProps<RootStackParamList, 'WeeklyAttendanceReport'>;

const PAGE_SIZE = 10;

export function WeeklyAttendanceReportScreen({ navigation }: Props) {
  const palette = useAppColors();
  const defaults = useMemo(() => getIsoWeekBounds(), []);
  const [week, setWeek] = useState(defaults);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [items, setItems] = useState<WeeklyAttendanceEmployeeReport[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canGoBack =
    typeof navigation.canGoBack === 'function' && navigation.canGoBack();

  const fetchReports = useCallback(
    async (nextPage: number, append: boolean, isRefresh = false) => {
      if (append) setLoadingMore(true);
      else if (!isRefresh) setLoading(true);
      setError(null);
      try {
        const result = await getWeeklyAttendanceReports({
          weekStart: week.weekStart,
          weekEnd: week.weekEnd,
          search: appliedSearch,
          page: nextPage,
          pageSize: PAGE_SIZE,
        });
        setItems(current => (append ? [...current, ...result.items] : result.items));
        setPage(result.pagination.page);
        setTotalPages(Math.max(result.pagination.totalPages, 1));
      } catch (caught) {
        setError(
          attendanceErrorMessage(caught, 'Unable to load weekly attendance reports.'),
        );
        if (!append) setItems([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
        setRefreshing(false);
      }
    },
    [appliedSearch, week.weekEnd, week.weekStart],
  );

  useEffect(() => {
    void fetchReports(1, false);
  }, [fetchReports]);

  return (
    <ReportAccessGuard reportCode={REPORT_ACCESS_CODES.WEEKLY_ATTENDANCE}>
      <>
        <AppScreen contentStyle={styles.screen} webLayout="wide">
          <ScreenHeader
            title="Weekly Attendance"
            onBack={canGoBack ? () => navigation.goBack() : undefined}
          />
          <View style={styles.body}>
            <View
              style={[
                styles.weekBar,
                { backgroundColor: palette.surface, borderColor: palette.border },
              ]}
            >
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Previous week"
                onPress={() => setWeek(current => shiftIsoWeek(current.weekStart, -1))}
                hitSlop={8}
              >
                <Text style={[styles.chevron, { color: palette.primary }]}>‹</Text>
              </Pressable>
              <Text style={[styles.weekLabel, { color: palette.textPrimary }]}>
                {formatWeekRangeLabel(week.weekStart, week.weekEnd)}
              </Text>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Next week"
                onPress={() => setWeek(current => shiftIsoWeek(current.weekStart, 1))}
                hitSlop={8}
              >
                <Text style={[styles.chevron, { color: palette.primary }]}>›</Text>
              </Pressable>
            </View>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="This week"
              onPress={() => setWeek(getIsoWeekBounds())}
            >
              <Text style={[styles.thisWeek, { color: palette.primary }]}>This week</Text>
            </Pressable>
            <TextInput
              accessibilityLabel="Search Employee"
              placeholder="Search Employee"
              placeholderTextColor={palette.textMuted}
              value={search}
              onChangeText={setSearch}
              onSubmitEditing={() => setAppliedSearch(search.trim())}
              returnKeyType="search"
              style={[
                styles.search,
                {
                  backgroundColor: palette.surface,
                  borderColor: palette.border,
                  color: palette.textPrimary,
                },
              ]}
            />

            {loading && items.length === 0 ? (
              <AppLoader message="Loading weekly attendance" />
            ) : error && items.length === 0 ? (
              <ErrorState message={error} onRetry={() => fetchReports(1, false)} />
            ) : (
              <FlatList
                data={items}
                keyExtractor={item => String(item.employeeId)}
                renderItem={({ item }) => (
                  <WeeklyAttendanceReportCard
                    report={item}
                    onPress={() =>
                      navigation.navigate(routes.weeklyAttendanceEmployee, {
                        employeeId: item.employeeId,
                        weekStart: week.weekStart,
                        weekEnd: week.weekEnd,
                      })
                    }
                  />
                )}
                ListEmptyComponent={
                  <EmptyState
                    title="No data available"
                    message="No employees matched this week and search."
                  />
                }
                refreshControl={
                  <RefreshControl
                    refreshing={refreshing}
                    onRefresh={() => {
                      setRefreshing(true);
                      void fetchReports(1, false, true);
                    }}
                    tintColor={palette.primary}
                  />
                }
                onEndReached={() => {
                  if (!loadingMore && page < totalPages) {
                    void fetchReports(page + 1, true);
                  }
                }}
                onEndReachedThreshold={0.4}
                ListFooterComponent={
                  loadingMore ? <ActivityIndicator color={palette.primary} /> : null
                }
                contentContainerStyle={styles.list}
              />
            )}
          </View>
        </AppScreen>
      </>
    </ReportAccessGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  body: { flex: 1, paddingBottom: spacing.lg },
  weekBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  weekLabel: { ...typography.body, fontWeight: '700', flex: 1, textAlign: 'center' },
  chevron: { fontSize: 28, fontWeight: '600', paddingHorizontal: spacing.sm },
  thisWeek: {
    ...typography.caption,
    fontWeight: '700',
    marginTop: spacing.sm,
    marginBottom: spacing.md,
  },
  search: {
    ...typography.body,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginBottom: spacing.md,
  },
  list: { paddingBottom: spacing.xxl },
});
